/**
 * Decodeert "Minified React error #NNN" naar een leesbare melding voor telemetrie.
 *
 * React strijkt in een productie-build de volledige fouttekst weg (alleen de dev-build
 * heeft 'm); je krijgt dan enkel `Minified React error #419; visit https://react.dev/errors/419 ...`.
 * Het nummer mapt 1:1 op een vaste melding, dus we hoeven niets te un-minifyen: we plakken
 * de leesbare tekst er hier bij vóór verzending naar App Insights. Zo lees je in het
 * dashboard direct wát er speelde, zonder de dev-React in productie te draaien.
 *
 * De map is bewust een gecureerde subset: de hydration/Suspense-cluster die in een SSR
 * Next-app verreweg het vaakst opduikt. Onbekende codes laten we ongemoeid (de
 * react.dev/errors-link zit al in de originele melding). Uitbreiden = een regel toevoegen.
 *
 * Bron: de runtime-meldingen van React 18.3.1 (`react-dom`). 419 en 425 zijn verbatim,
 * 421/422/423/426 zijn tot één regel ingekorte parafrases. De volledige tekst + eventuele
 * `?args[]=`-context staan achter de react.dev-link in de originele melding. Let op bij een
 * React-major-upgrade: een code kan herwoord zijn (418 verschilt al tussen 18.x en het
 * main-codes.json), herijk de teksten dan.
 */
export const REACT_ERROR_MESSAGES: Record<string, string> = {
  '418': 'Hydration failed because the initial UI does not match what was rendered on the server.',
  '419':
    'The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering.',
  '421':
    'This Suspense boundary received an update before it finished hydrating, switching it to client rendering. Wrap the update in startTransition.',
  '422':
    'There was an error while hydrating but React recovered by client-rendering from the nearest Suspense boundary.',
  '423':
    'There was an error while hydrating but React recovered by client-rendering the entire root.',
  '425': 'Text content does not match server-rendered HTML.',
  '426':
    'A component suspended while responding to synchronous input, replacing the UI with a loading indicator. Wrap suspending updates in startTransition.',
}

/**
 * Plakt de leesbare melding achter een "Minified React error #NNN"-tekst.
 * Laat onbekende codes en niet-React-meldingen ongemoeid en is idempotent
 * (verwerkt dezelfde melding twee keer => identiek resultaat).
 */
export function decodeReactError(message: string): string {
  const match = message.match(/Minified React error #(\d+)/)
  if (!match) return message

  const code = match[1]
  const full = REACT_ERROR_MESSAGES[code]
  if (!full) return message

  const marker = `[React #${code}: `
  if (message.includes(marker)) return message // al gedecodeerd

  return `${message} ${marker}${full}]`
}
